/**
 * Blossom Wings, Cart Drawer + Cart Page Controller
 *
 * UX pattern: header cart icon → /cart, add-to-bag → drawer.
 * In-place updates everywhere, no reloads.
 *
 * Inventory-aware: when Shopify rejects qty change because stock is
 * insufficient, we (1) refresh cart to reset optimistic UI, (2) show a
 * brief toast with the inventory message. Prevents the "button looks broken"
 * symptom when really it's stock blocking the change.
 *
 * Debug logs gated behind:
 *   localStorage.setItem('bw_cart_debug', '1') OR ?cart_debug=1 in URL.
 */
(function() {
  'use strict';

  var DEBUG = window.BW_CART_DEBUG === true
    || /[?&]cart_debug=1/.test(window.location.search)
    || (function() { try { return localStorage.getItem('bw_cart_debug') === '1'; } catch (e) { return false; } })();
  function log()  { if (DEBUG) { var a = ['[bw-cart]'].concat([].slice.call(arguments)); console.log.apply(console, a); } }
  function warn() { var a = ['[bw-cart]'].concat([].slice.call(arguments)); console.warn.apply(console, a); }
  function err()  { var a = ['[bw-cart]'].concat([].slice.call(arguments)); console.error.apply(console, a); }

  log('script init');

  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) { warn('drawer [data-cart-drawer] not found, skipping'); return; }
  log('drawer found');

  let i18n = {};
  try {
    if (drawer.dataset.i18n) i18n = JSON.parse(drawer.dataset.i18n);
  } catch (e) { warn('i18n parse failed:', e); }

  const itemsEl = drawer.querySelector('[data-cart-items]');
  const footEl = drawer.querySelector('[data-cart-foot]');
  const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
  const countEl = drawer.querySelector('[data-cart-count]');
  const threshold = parseInt(drawer.dataset.threshold) || 6521;
  const cartUrl = drawer.dataset.cartUrl || '/cart';
  const isCartPage = window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart');

  // ===== Toast (inventory-aware error UI) =====
  function showToast(message, kind) {
    kind = kind || 'info';
    var toast = document.createElement('div');
    toast.className = 'bw-toast bw-toast--' + kind;
    toast.textContent = message;
    toast.setAttribute('role', 'alert');
    document.body.appendChild(toast);
    requestAnimationFrame(function() { toast.classList.add('is-visible'); });
    setTimeout(function() {
      toast.classList.remove('is-visible');
      setTimeout(function() { toast.remove(); }, 350);
    }, 3500);
  }

  // ===== Drawer open/close =====
  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bw-no-scroll');
  }
  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bw-no-scroll');
  }
  document.addEventListener('click', function(e) {
    if (e.target.closest('[data-drawer-close]')) closeDrawer();
  });
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  // ===== Add to cart =====
  document.addEventListener('submit', async function(e) {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalLabel = submitBtn ? submitBtn.textContent : null;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }
    try {
      const res = await fetch(cartUrl + '/add.js', { method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' } });
      if (!res.ok) {
        let data = null;
        try { data = await res.json(); } catch (e2) {}
        const msg = (data && (data.description || data.message)) || ('Could not add to bag (' + res.status + ')');
        log('add.js error', { status: res.status, data: data });
        showToast(msg, 'error');
        await refreshCart();
        if (submitBtn) { submitBtn.textContent = 'Try again'; submitBtn.disabled = false; setTimeout(function() { if (submitBtn) submitBtn.textContent = originalLabel; }, 2000); }
        return;
      }
      log('add.js ok');
      // Cart page rows are server-rendered; a new line (e.g. from the cart cross-sell)
      // only shows after a reload, so reload instead of opening the drawer there.
      if (isCartPage) { window.location.reload(); return; }
      await refreshCart();
      openDrawer();
      if (submitBtn) {
        submitBtn.textContent = '✓ Added';
        setTimeout(function() { submitBtn.textContent = originalLabel; submitBtn.disabled = false; }, 1500);
      }
    } catch (e2) {
      err('add to cart error', e2);
      showToast('Could not add to bag, please try again.', 'error');
      if (submitBtn) { submitBtn.textContent = 'Try again'; submitBtn.disabled = false; }
    }
  });

  // ===== Quantity change & remove (delegated) =====
  document.addEventListener('click', async function(e) {
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const item = qtyBtn.closest('[data-cart-item]');
      if (!item) return;
      e.preventDefault();
      const input = item.querySelector('[data-qty-input]');
      if (!input) return;
      const currentQty = parseInt(input.value, 10) || 1;
      const change = parseInt(qtyBtn.dataset.qtyChange, 10) || 0;
      const newQty = Math.max(0, currentQty + change);
      log('qty click', { key: item.dataset.key, currentQty: currentQty, change: change, newQty: newQty });
      input.value = newQty;
      qtyBtn.disabled = true;
      try {
        await updateLine(item.dataset.key, newQty);
      } finally {
        qtyBtn.disabled = false;
      }
      return;
    }
    const removeBtn = e.target.closest('[data-remove]');
    if (removeBtn) {
      const item = removeBtn.closest('[data-cart-item]');
      if (!item) return;
      e.preventDefault();
      await updateLine(item.dataset.key, 0);
    }
  });

  document.addEventListener('change', async function(e) {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const item = input.closest('[data-cart-item]');
    if (!item) return;
    await updateLine(item.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
  });

  async function updateLine(key, quantity) {
    if (!key) { warn('updateLine missing key'); return; }
    try {
      log('POST /cart/change.js', { id: key, quantity: quantity });
      const res = await fetch(cartUrl + '/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!res.ok) {
        let data = null;
        try { data = await res.json(); } catch (e2) {}
        const msg = (data && (data.description || data.message)) || ('Could not update cart (' + res.status + ')');
        log('change.js error', { status: res.status, data: data });
        showToast(msg, 'error');
        await refreshCart(); // critical: reset optimistic UI to actual cart state
        return;
      }
      const data = await res.json();
      log('change.js ok', { item_count: data.item_count, total_price: data.total_price });
      await refreshCart();
    } catch (e2) {
      err('updateLine error', e2);
      showToast('Could not update cart, please try again.', 'error');
      await refreshCart();
    }
  }

  async function refreshCart() {
    try {
      const cart = await (await fetch(cartUrl + '.js', { headers: { 'Accept': 'application/json' } })).json();
      log('cart fetched', { item_count: cart.item_count, total_price: cart.total_price });
      renderDrawer(cart);
      renderCartPage(cart);
      updateShippingBars(cart.total_price);
      updateHeaderBadge(cart.item_count);
      // Cart cross-sell (snippets/bw-cart-xsell) re-picks its suggestion from this.
      window.bwCartState = cart;
      try { document.dispatchEvent(new CustomEvent('bw:cart:refreshed', { detail: cart })); } catch (e3) {}
    } catch (e2) {
      err('refreshCart error', e2);
    }
  }

  // Store prices are stored net of VAT (Shopify adds 15% at checkout), but every
  // price we show the shopper is the VAT-inclusive shelf price. Every caller here
  // passes net cents straight from the cart API, so gross up in one place.
  function grossCents(cents) { return Math.round(cents * 115 / 100); }

  function formatMoney(cents) {
    const amt = (grossCents(cents) / 100).toFixed(2);
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SAR';
    return amt + ' ' + code;
  }
  function escapeHtml(str) { const div = document.createElement('div'); div.textContent = str || ''; return div.innerHTML; }

  function updateHeaderBadge(count) {
    const headerCart = document.querySelector('.bw-header__cart');
    let headerBadge = document.querySelector('.bw-header__cart-count');
    if (count > 0) {
      if (!headerBadge && headerCart) {
        headerBadge = document.createElement('span');
        headerBadge.className = 'bw-header__cart-count';
        headerCart.appendChild(headerBadge);
      }
      if (headerBadge) { headerBadge.textContent = count; headerBadge.style.display = ''; }
    } else if (headerBadge) {
      headerBadge.style.display = 'none';
    }
  }

  function renderDrawer(cart) {
    if (!itemsEl || !footEl) return;
    if (countEl) countEl.textContent = cart.item_count;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `
        <div class="bw-cart-empty">
          <div class="bw-cart-empty__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M6 7h12l-1.2 11.4a2 2 0 01-2 1.6H9.2a2 2 0 01-2-1.6L6 7z"/>
              <path d="M9 10V6a3 3 0 016 0v4"/>
            </svg>
          </div>
          <h3>${i18n.emptyTitle || 'Your bag is empty'}</h3>
          <p>${i18n.emptyMessage || 'Add some softness to your day.'}</p>
          <a href="${i18n.collectionsUrl || '/collections/all'}" class="bw-btn bw-btn--primary" data-drawer-close>${i18n.emptyCta || 'Shop the collection'}</a>
        </div>
      `;
      footEl.hidden = true;
    } else {
      itemsEl.innerHTML = cart.items.map(function(item) { return `
        <article class="bw-cart-item" data-cart-item data-key="${item.key}">
          <a href="${item.url}" class="bw-cart-item__media">
            ${item.image ? `<img src="${item.image}" width="100" height="100" alt="${escapeHtml(item.product_title)}" class="bw-cart-item__img" loading="lazy">` : ''}
          </a>
          <div class="bw-cart-item__body">
            <a href="${item.url}" class="bw-cart-item__title">${escapeHtml(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="bw-cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
            <div class="bw-cart-item__bottom">
              <div class="bw-cart-item__qty">
                <button type="button" class="bw-cart-item__qty-btn" data-qty-change="-1" aria-label="Decrease">−</button>
                <input type="number" class="bw-cart-item__qty-input" value="${item.quantity}" min="0" data-qty-input>
                <button type="button" class="bw-cart-item__qty-btn" data-qty-change="1" aria-label="Increase">+</button>
              </div>
              <span class="bw-cart-item__price">${item.original_line_price > item.final_line_price ? '<s class="bw-cart-item__was">' + formatMoney(item.original_line_price) + '</s> ' : ''}${formatMoney(item.final_line_price)}</span>
            </div>
            <button type="button" class="bw-cart-item__remove" data-remove>${i18n.remove || 'Remove'}</button>
          </div>
        </article>
      `; }).join('');
      footEl.hidden = false;
    }
  }

  function renderCartPage(cart) {
    if (!isCartPage) return;
    const rows = document.querySelectorAll('[data-cart-item][data-cart-page-row]');
    rows.forEach(function(row) {
      const key = row.dataset.key;
      const vid = row.dataset.variantId;
      // Match by line key first; fall back to variant id because Shopify line
      // keys are NOT guaranteed stable across change.js calls. Without the
      // fallback a shifted key makes find() miss and the row gets wrongly removed.
      let item = cart.items.find(function(i) { return i.key === key; });
      if (!item && vid) {
        item = cart.items.find(function(i) { return String(i.variant_id || i.id) === vid; });
      }
      if (!item) { row.remove(); return; } // genuinely gone from the cart
      // Self-heal: store the current key so the next qty update posts a valid id.
      if (item.key && item.key !== key) row.dataset.key = item.key;
      const totalEl = row.querySelector('[data-cart-line-total]');
      if (totalEl) totalEl.textContent = formatMoney(item.final_line_price);
      const qtyInput = row.querySelector('[data-qty-input]');
      if (qtyInput && document.activeElement !== qtyInput) qtyInput.value = item.quantity;
      const unitEl = row.querySelector('[data-cart-line-unit]');
      if (unitEl) unitEl.textContent = formatMoney(item.final_price);
    });
    const pageSubtotal = document.querySelector('[data-cart-page-subtotal]');
    if (pageSubtotal) pageSubtotal.textContent = formatMoney(cart.total_price);
    const pageTotal = document.querySelector('[data-cart-page-total]');
    if (pageTotal) pageTotal.textContent = formatMoney(cart.total_price);
    const checkoutTotal = document.querySelector('[data-cart-page-checkout-total]');
    if (checkoutTotal) checkoutTotal.textContent = '· ' + formatMoney(cart.total_price);
    if (cart.item_count === 0) {
      const layout = document.querySelector('.bw-cart-page__layout');
      const shipping = document.querySelector('.bw-cart-page__shipping');
      const empty = document.querySelector('[data-cart-page-empty]');
      if (layout) layout.style.display = 'none';
      if (shipping) shipping.style.display = 'none';
      if (empty) empty.style.display = '';
      const continueLink = document.querySelector('.bw-cart-page__continue');
      if (continueLink) continueLink.style.display = 'none';
      const headerCount = document.querySelector('.bw-cart-page__count');
      if (headerCount) headerCount.style.display = 'none';
    }
  }

  function updateShippingBars(total) {
    if (threshold <= 0) return;
    document.querySelectorAll('[data-shipping-progress]').forEach(function(node) {
      const msg = node.querySelector('[data-shipping-msg]');
      const fill = node.querySelector('[data-shipping-fill]');
      if (fill) fill.style.width = Math.min(100, (total / threshold) * 100) + '%';
      if (msg) {
        if (total >= threshold) {
          msg.innerHTML = i18n.shippingUnlocked || '🌸 You unlocked <strong>free shipping</strong>!';
        } else {
          // Gross threshold minus the gross (half-up) total shown, so the printed numbers subtract exactly.
          const remainingCents = Math.max(0, grossCents(threshold) - grossCents(total));
          const remaining = (remainingCents / 100).toFixed(2) + ' ' + ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SAR');
          const template = i18n.shippingRemaining || 'Add <strong>__AMOUNT__</strong> more for free shipping.';
          msg.innerHTML = template.replace('__AMOUNT__', remaining);
        }
      }
    });
  }

  refreshCart();
  log('ready');
})();

// Toast styles, injected once, lives in same asset
(function() {
  if (document.getElementById('bw-toast-styles')) return;
  var s = document.createElement('style');
  s.id = 'bw-toast-styles';
  s.textContent = `
.bw-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: #1a2348;
  color: #fff;
  padding: 14px 22px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.4;
  max-width: calc(100% - 32px);
  box-shadow: 0 8px 28px rgba(0,0,0,0.2);
  opacity: 0;
  transition: opacity 0.25s ease, transform 0.25s ease;
  z-index: 9999;
  pointer-events: none;
}
.bw-toast.is-visible { opacity: 1; transform: translateX(-50%) translateY(0); }
.bw-toast--error { background: #b54a3a; }
.bw-toast--success { background: #2a7a4f; }
@media (max-width: 600px) {
  .bw-toast { bottom: 16px; padding: 12px 18px; font-size: 13.5px; }
}
`;
  document.head.appendChild(s);
})();
